import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'

import { cn } from '@/lib/cn'

export interface SelectOption {
  description?: string
  icon?: ReactNode
  label: string
  trailing?: ReactNode
  value: string
}

export interface SelectProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'onChange' | 'value'
> {
  invalid?: boolean
  onValueChange: (value: string) => void
  options: SelectOption[]
  // 只读态保留焦点与读屏可达，只是不能展开改值；disabled 会把控件从可访问性树里移除。
  readOnly?: boolean
  value: string
}

interface ListboxPosition {
  style: CSSProperties
}

interface ScrollableOption {
  scrollIntoView?: (options?: ScrollIntoViewOptions) => void
}

const listboxGap = 4
const listboxMaxHeight = 224
const viewportPadding = 8

function optionId(listboxId: string, index: number) {
  return `${listboxId}-option-${String(index)}`
}

export function Select({
  className,
  disabled,
  invalid = false,
  onValueChange,
  options,
  readOnly = false,
  value,
  ...props
}: SelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const [listboxPosition, setListboxPosition] = useState<ListboxPosition | null>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const listboxRef = useRef<HTMLDivElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const listboxId = useId()
  const selectedOption =
    options.find((option) => option.value === value) ??
    (options.length > 0 ? options[0] : { label: '', value: '' })
  const activeOptionId = isOpen && activeIndex >= 0 ? optionId(listboxId, activeIndex) : undefined

  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    function handlePointerDown(event: PointerEvent) {
      const target = event.target as Node
      if (!rootRef.current?.contains(target) && !listboxRef.current?.contains(target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
    }
  }, [isOpen])

  useLayoutEffect(() => {
    if (!isOpen) {
      return undefined
    }

    function updateListboxPosition() {
      const trigger = buttonRef.current
      if (!trigger) return

      const rect = trigger.getBoundingClientRect()
      const belowSpace = window.innerHeight - rect.bottom - listboxGap - viewportPadding
      const aboveSpace = rect.top - listboxGap - viewportPadding
      const openUpwards = belowSpace < Math.min(160, listboxMaxHeight) && aboveSpace > belowSpace
      const availableHeight = openUpwards ? aboveSpace : belowSpace
      const maxHeight = Math.max(72, Math.min(listboxMaxHeight, availableHeight))
      const width = Math.min(rect.width, window.innerWidth - viewportPadding * 2)
      const left = Math.max(
        viewportPadding,
        Math.min(rect.left, window.innerWidth - width - viewportPadding),
      )

      setListboxPosition({
        style: {
          ...(openUpwards
            ? { bottom: window.innerHeight - rect.top + listboxGap }
            : { top: rect.bottom + listboxGap }),
          left,
          maxHeight,
          width,
        },
      })
    }

    updateListboxPosition()
    window.addEventListener('resize', updateListboxPosition)
    window.addEventListener('scroll', updateListboxPosition, true)

    return () => {
      window.removeEventListener('resize', updateListboxPosition)
      window.removeEventListener('scroll', updateListboxPosition, true)
    }
  }, [isOpen])

  useLayoutEffect(() => {
    if (!isOpen || !listboxPosition || activeIndex < 0) return

    const activeOption = document.getElementById(
      optionId(listboxId, activeIndex),
    ) as ScrollableOption | null
    activeOption?.scrollIntoView?.({ block: 'nearest' })
  }, [activeIndex, isOpen, listboxId, listboxPosition])

  function closeListbox() {
    setIsOpen(false)
    setActiveIndex(-1)
  }

  function openListbox() {
    if (options.length === 0) return

    const selectedIndex = options.findIndex((option) => option.value === value)
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0)
    setIsOpen(true)
  }

  function moveActiveOption(direction: -1 | 1) {
    setActiveIndex((current) => {
      const next = current < 0 ? 0 : current + direction
      return Math.max(0, Math.min(options.length - 1, next))
    })
  }

  function selectOption(nextValue: string) {
    onValueChange(nextValue)
    closeListbox()
    buttonRef.current?.focus()
  }

  return (
    <div
      className={cn(
        'relative h-[38px] w-full text-sm',
        disabled ? 'cursor-not-allowed' : null,
        className,
      )}
      ref={rootRef}
    >
      <button
        aria-activedescendant={activeOptionId}
        aria-controls={isOpen ? listboxId : undefined}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        className={cn(
          'flex h-full w-full min-w-0 items-center rounded-lg border pl-3 pr-9 text-left shadow-[var(--shadow-control)] transition-colors outline-none',
          disabled
            ? 'cursor-not-allowed border-[var(--color-panel-border)] bg-[var(--color-surface-muted)] text-[var(--color-text-muted)] shadow-none'
            : [
                'cursor-pointer bg-white',
                selectedOption.value === ''
                  ? 'text-[var(--color-control-placeholder)]'
                  : 'text-[var(--color-text)]',
                'focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary-soft)]',
                invalid
                  ? 'border-[var(--color-danger)]'
                  : 'border-[var(--color-control-border)] hover:border-[var(--color-control-border-hover)]',
              ],
        )}
        aria-readonly={readOnly || undefined}
        disabled={disabled}
        {...props}
        onClick={(event) => {
          props.onClick?.(event)
          if (readOnly) {
            return
          }
          if (isOpen) {
            closeListbox()
          } else {
            openListbox()
          }
        }}
        onKeyDown={(event) => {
          props.onKeyDown?.(event)

          if (event.key === 'Tab') {
            if (isOpen) closeListbox()
            return
          }

          if (event.key === 'Escape') {
            if (isOpen) {
              event.preventDefault()
              closeListbox()
            }
            return
          }

          if (readOnly) {
            return
          }

          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault()
            if (isOpen) {
              moveActiveOption(event.key === 'ArrowDown' ? 1 : -1)
            } else {
              openListbox()
            }
            return
          }

          if (isOpen && (event.key === 'Home' || event.key === 'End')) {
            event.preventDefault()
            setActiveIndex(event.key === 'Home' ? 0 : options.length - 1)
            return
          }

          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            if (!isOpen) {
              openListbox()
              return
            }

            const activeOption = options.at(activeIndex)
            if (activeOption) selectOption(activeOption.value)
          }
        }}
        ref={buttonRef}
        role="combobox"
        type="button"
      >
        {selectedOption.icon ? (
          <span
            className={cn(
              'mr-2 flex size-5 shrink-0 items-center justify-center',
              disabled ? 'opacity-60 grayscale' : null,
            )}
          >
            {selectedOption.icon}
          </span>
        ) : null}
        <span className="block min-w-0 flex-1 truncate" title={selectedOption.label || undefined}>
          {selectedOption.label}
        </span>
        {selectedOption.trailing ? (
          <span className="ml-2 mr-1 flex shrink-0 items-center">{selectedOption.trailing}</span>
        ) : null}
      </button>
      <svg
        aria-hidden
        className={cn(
          'pointer-events-none absolute top-1/2 right-3 size-3 -translate-y-1/2 transition-transform',
          disabled ? 'text-[rgba(26,28,25,0.28)]' : 'text-[var(--color-text-muted)]',
          isOpen ? 'rotate-180' : null,
        )}
        fill="none"
        viewBox="0 0 12 12"
      >
        <path
          d="m3 4.5 3 3 3-3"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.5"
        />
      </svg>
      {isOpen && listboxPosition
        ? createPortal(
            <div
              className="fixed z-[120] overflow-auto rounded-md border border-[var(--color-panel-border)] bg-white py-1 shadow-[0_10px_30px_rgba(26,28,25,0.12)]"
              id={listboxId}
              ref={listboxRef}
              role="listbox"
              style={listboxPosition.style}
            >
              {options.map((option, index) => (
                <button
                  aria-selected={option.value === value}
                  className={cn(
                    'flex w-full cursor-pointer items-center gap-2 px-3 text-left text-sm text-[var(--color-text)] hover:bg-[var(--color-hover-muted)]',
                    option.description ? 'min-h-[52px] py-2' : 'h-9 whitespace-nowrap',
                    index === activeIndex
                      ? 'bg-[rgba(30,95,255,0.08)] text-[var(--color-primary)]'
                      : null,
                  )}
                  id={optionId(listboxId, index)}
                  key={option.value}
                  onClick={() => {
                    selectOption(option.value)
                  }}
                  onPointerDown={(event) => event.preventDefault()}
                  onPointerMove={() => setActiveIndex(index)}
                  role="option"
                  tabIndex={-1}
                  type="button"
                >
                  {option.icon ? (
                    <span className="flex size-5 shrink-0 items-center justify-center">
                      {option.icon}
                    </span>
                  ) : null}
                  <span className="min-w-0 flex-1">
                    <span
                      className={cn('block truncate', option.description ? 'font-medium' : null)}
                      title={option.label}
                    >
                      {option.label}
                    </span>
                    {option.description ? (
                      <span
                        className="mt-0.5 block truncate text-xs leading-4 text-[var(--color-text-muted)]"
                        title={option.description}
                      >
                        {option.description}
                      </span>
                    ) : null}
                  </span>
                  {option.trailing ? (
                    <span className="ml-auto flex shrink-0 items-center">{option.trailing}</span>
                  ) : null}
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </div>
  )
}
